import type {
	PlayerContract,
	ReleasedPlayerWithoutKey,
} from "../../../common/types.ts";
import { PLAYER } from "../../../common/constants.ts";
import { helpers } from "../../util/index.ts";
import oldAbbrevTo2020BBGMAbbrev from "./oldAbbrevTo2020BBGMAbbrev.ts";

// ============================================================================
// MANUAL TRANSACTIONS — real-NBA moves the roster seed doesn't carry yet.
//
// One line per transaction. Applied when a real league is generated. Write the
// plain-English move as a comment above each entry so this file reads like a
// transaction log.
//
//   • A team cut a player who's owed money  -> DEAD_MONEY  (dead cap; auto-waives him)
//   • A player signed / re-signed / traded  -> ROSTER_MOVES (team + contract)
//
// slug = Basketball-Reference id (same key the data file uses / Player.srID).
// abbrev = BBGM team code (e.g. "LAL", "SAC"). Dollars are FULL dollars.
// exp = ending-year of the last season (a 2026-27 season is exp 2027).
// ============================================================================

// ---- shared lookups --------------------------------------------------------

type MinimalPlayer = {
	pid: number;
	tid: number;
	srID?: string;
	contract?: PlayerContract;
};

const tidByAbbrev = (initialTeams: { tid: number; srID?: string }[]) => {
	const map = new Map<string, number>();
	for (const t of initialTeams) {
		if (t.srID !== undefined) {
			map.set(oldAbbrevTo2020BBGMAbbrev(t.srID), t.tid);
		}
	}
	return map;
};

const playerBySlug = (players: MinimalPlayer[]) => {
	const map = new Map<string, MinimalPlayer>();
	for (const p of players) {
		if (p.srID !== undefined) {
			map.set(p.srID, p);
		}
	}
	return map;
};

// ---- dead money (cuts) -----------------------------------------------------

export type DeadMoneyEntry = {
	slug: string;
	abbrev: string; // team that still owes the money
	amount: number; // FLAT annual dead-cap charge, in FULL dollars
	exp: number; // last season owed (stretched deal -> same amount, later exp)
};

export const DEAD_MONEY: Record<number, DeadMoneyEntry[]> = {
	2026: [
		// Kings cut DeMar DeRozan; they eat $10M of dead money this season.
		{ slug: "derozde01", abbrev: "SAC", amount: 10_000_000, exp: 2027 },
	],
};

// Synthesize releasedPlayers records. Waives a player into free agency when he is still on the
// team that owes the money (a team can't carry dead money for a player on its active roster).
export const buildReleasedPlayers = (
	season: number,
	players: MinimalPlayer[],
	initialTeams: { tid: number; srID?: string }[],
): ReleasedPlayerWithoutKey[] => {
	const entries = DEAD_MONEY[season];
	if (!entries || entries.length === 0) {
		return [];
	}

	const teams = tidByAbbrev(initialTeams);
	const bySlug = playerBySlug(players);

	const released: ReleasedPlayerWithoutKey[] = [];
	for (const entry of entries) {
		const tid = teams.get(entry.abbrev);
		const p = bySlug.get(entry.slug);
		if (tid === undefined || !p) {
			continue;
		}

		if (p.tid === tid) {
			p.tid = PLAYER.FREE_AGENT;
		}

		released.push({
			pid: p.pid,
			tid,
			contract: {
				// Data-file salaries are stored in thousands (dollars / 1000).
				amount: helpers.roundContract(entry.amount / 1000),
				exp: entry.exp,
			},
		});
	}

	return released;
};

// ---- roster moves (signings / re-signings / trades) ------------------------

export type RosterMove = {
	slug: string;
	abbrev: string; // team he ends up on
	// Per-season salary in FULL dollars, contiguous from the upcoming season. Omit for a pure
	// trade where the contract doesn't change.
	amounts?: number[];
	// Ending-year of the last salary. Required when `amounts` is given (2yr deal from 2026-27 -> 2028).
	exp?: number;
};

export const ROSTER_MOVES: Record<number, RosterMove[]> = {
	2026: [
		// Rui Hachimura agreed to a 2-year, $28M deal with the Lakers ($14M/yr).
		{ slug: "hachiru01", abbrev: "LAL", amounts: [14_000_000, 14_000_000], exp: 2028 },
		// Donovan Mitchell re-signed with the Cavs on an escalating extension.
		// This overwrites his whole deal, so slot 0 keeps his existing 2026-27
		// salary ($50.1M); the extension re-prices 2027-28 onward:
		//   2026-27 $50.1M · 2027-28 $61M · 2028-29 $65M · 2029-30 $70M · 2030-31 $75M
		{
			slug: "mitchdo01",
			abbrev: "CLE",
			amounts: [50_105_628, 61_000_000, 65_000_000, 70_000_000, 75_000_000],
			exp: 2031,
		},
	],
};

// Place players on their new team and overwrite their contract. Mutates the formatted players.
export const applyRosterMoves = (
	season: number,
	players: MinimalPlayer[],
	initialTeams: { tid: number; srID?: string }[],
) => {
	const moves = ROSTER_MOVES[season];
	if (!moves || moves.length === 0) {
		return;
	}

	const teams = tidByAbbrev(initialTeams);
	const bySlug = playerBySlug(players);

	for (const move of moves) {
		const tid = teams.get(move.abbrev);
		const p = bySlug.get(move.slug);
		if (tid === undefined || !p) {
			continue;
		}

		p.tid = tid;

		if (move.amounts && move.amounts.length > 0 && move.exp !== undefined) {
			const amounts = move.amounts.map((a) => helpers.roundContract(a / 1000));
			p.contract = {
				amount: amounts[0]!,
				exp: move.exp,
				amounts,
			};
		}
	}
};
